import React, { useRef, useCallback } from 'react';
import { Input } from '../../ui/input';

/**
 * DateInput - Enhanced date input with:
 * - Year range clamping (2000-2099) to prevent accidental entries like 0005
 * - Tab key handling to skip browser's internal calendar picker tab stop
 * - Context date: auto-fills empty field on focus so calendar opens at the right month
 */
const DateInput = (props) => {
  const { value, onChange, contextDate, className = '' } = props;
  const inputRef = useRef(null);

  const handleFocus = useCallback(() => {
    if (!value && contextDate) {
      onChange({ target: { value: contextDate } });
    }
  }, [value, contextDate, onChange]);

  // On blur, validate and clamp the year
  const handleBlur = useCallback((e) => {
    const val = e.target.value;
    if (!val) return;

    const parts = val.split('-');
    if (parts.length === 3) {
      let year = parseInt(parts[0], 10);
      if (isNaN(year) || year < 2000) {
        year = 2000 + (year % 100 || 0);
        if (year < 2000) year = 2024;
        if (year > 2099) year = 2099;
        const corrected = year + '-' + parts[1] + '-' + parts[2];
        onChange({ target: { value: corrected } });
      } else if (year > 2099) {
        const corrected = '2099-' + parts[1] + '-' + parts[2];
        onChange({ target: { value: corrected } });
      }
    }

    if (props.onBlur) props.onBlur(e);
  }, [onChange, props]);

  // On Tab, skip the browser's calendar picker icon by focusing next input directly
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      const form = inputRef.current?.closest('form') || inputRef.current?.closest('[role="dialog"]');
      if (!form) return;

      const focusable = Array.from(
        form.querySelectorAll('input:not([type="checkbox"]):not([type="hidden"]):not([tabindex="-1"]), textarea, select')
      ).filter(function(el) { return !el.disabled && el.offsetParent !== null; });

      const currentIndex = focusable.indexOf(inputRef.current);
      if (currentIndex >= 0 && currentIndex < focusable.length - 1) {
        e.preventDefault();
        focusable[currentIndex + 1].focus();
      }
    }

    if (props.onKeyDown) props.onKeyDown(e);
  }, [props]);

  return (
    <Input
      ref={inputRef}
      type="date"
      value={value}
      onChange={onChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      min="2000-01-01"
      max="2099-12-31"
      className={className}
      required={props.required}
      data-testid={props['data-testid']}
    />
  );
};

export default DateInput;
